import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';

const AUDIENCE_OPTIONS = [
    { v: 'Corporate', l: 'Corporate / CSR' },
    { v: 'Hotel', l: 'Hotel / Hospitality' },
    { v: 'University', l: 'University / School' },
    { v: 'Community', l: 'NGO / Community' },
    { v: 'Individual', l: 'Individual' },
    { v: 'Volunteer', l: 'Volunteer' },
    { v: 'Media', l: 'Media / Press' },
    { v: 'Other', l: 'Other' },
];

const INTEREST_OPTIONS = [
    { v: 'agritourism', l: 'Agritourism & Rural Experiences' },
    { v: 'gatherings', l: 'Community Gatherings & Fundraising' },
    { v: 'volunteering', l: 'Volunteering & Skill-Based Giving' },
    { v: 'campaigns', l: 'Impact Campaigns' },
    { v: 'other', l: "Something else — let's talk" },
];

const initial = {
    name: '', email: '', organization: '',
    audience_type: 'Corporate', interest: 'agritourism', message: '',
};

export default function Partner() {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
            toast.error('Please fill in your name, email and a short message.');
            return;
        }
        setSubmitting(true);
        try {
            await api.post('/contact', form);
            setSubmitted(true);
            setForm(initial);
            toast.success('Thank you — we will be in touch within two working days.');
        } catch (err) {
            const msg = err?.response?.data?.detail?.[0]?.msg || 'Something went wrong. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Please check the form and try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="partner-page">
            {/* Hero */}
            <section className="pt-40 pb-16 md:pt-52 md:pb-24 bg-aurika-linen border-b border-aurika-border">
                <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <p className="overline">Partner With Aurika</p>
                        <h1 className="mt-6 font-display font-light text-aurika-obsidian leading-[0.98] tracking-[-0.02em]
                            text-[3rem] sm:text-[4.25rem] md:text-[5.25rem] lg:text-[6rem]">
                            Let’s design <em className="italic text-aurika-terracotta">something</em> the world needs more of.
                        </h1>
                    </div>
                    <div className="lg:col-span-4 lg:pt-6 text-aurika-ink font-light">
                        <p>
                            Tell us who you are and what you’re hoping to create. Whether it’s a corporate offsite in a village, a supper club with soul, or a campaign to replace single-use gifting — we’d love to think it through with you.
                        </p>
                    </div>
                </div>
            </section>

            {/* Form */}
            <section className="section-y bg-aurika-bone">
                <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <p className="overline">Start The Conversation</p>
                        <h2 className="mt-4 font-display text-4xl md:text-5xl font-light text-aurika-obsidian leading-[1.05]">
                            Write to us.
                        </h2>
                        <div className="mt-10 space-y-6 text-aurika-ink font-light">
                            <div>
                                <p className="overline !text-aurika-obsidian !text-[0.65rem]">Email</p>
                                <p className="mt-2">aurika.foundation@gmail.com</p>
                            </div>
                            <div>
                                <p className="overline !text-aurika-obsidian !text-[0.65rem]">Phone</p>
                                <p className="mt-2">+91-9999083360</p>
                            </div>
                            <div>
                                <p className="overline !text-aurika-obsidian !text-[0.65rem]">Studio</p>
                                <p className="mt-2">Delhi, India</p>
                            </div>
                            <div>
                                <p className="overline !text-aurika-obsidian !text-[0.65rem]">Response Window</p>
                                <p className="mt-2">Within two working days.</p>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-8">
                        {submitted ? (
                            <div data-testid="partner-form-success" className="border border-aurika-borderStrong p-10 md:p-14 bg-aurika-linen">
                                <p className="overline">Received — with gratitude</p>
                                <h3 className="mt-4 font-display text-4xl md:text-5xl font-light text-aurika-obsidian leading-[1.05]">
                                    Thank you for reaching out.
                                </h3>
                                <p className="mt-6 text-aurika-ink font-light">
                                    A member of our team will be in touch within two working days. In the meantime, feel free to explore our verticals or stories from the ground.
                                </p>
                                <button
                                    data-testid="partner-form-reset"
                                    onClick={() => setSubmitted(false)}
                                    className="btn-outline mt-10"
                                >
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form data-testid="partner-form" onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
                                <div className="md:col-span-1">
                                    <label className="overline block">Your name</label>
                                    <input
                                        data-testid="partner-input-name"
                                        name="name" value={form.name} onChange={onChange} required
                                        className="field-line mt-3" placeholder="Ananya Menon"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="overline block">Email</label>
                                    <input
                                        data-testid="partner-input-email"
                                        type="email" name="email" value={form.email} onChange={onChange} required
                                        className="field-line mt-3" placeholder="ananya@company.com"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="overline block">Organisation (optional)</label>
                                    <input
                                        data-testid="partner-input-org"
                                        name="organization" value={form.organization} onChange={onChange}
                                        className="field-line mt-3" placeholder="Your company, university or collective"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="overline block">You are a…</label>
                                    <select
                                        data-testid="partner-select-audience"
                                        name="audience_type" value={form.audience_type} onChange={onChange}
                                        className="field-line mt-3 bg-transparent"
                                    >
                                        {AUDIENCE_OPTIONS.map((o) => (
                                            <option key={o.v} value={o.v}>{o.l}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="md:col-span-1">
                                    <label className="overline block">Interested in</label>
                                    <select
                                        data-testid="partner-select-interest"
                                        name="interest" value={form.interest} onChange={onChange}
                                        className="field-line mt-3 bg-transparent"
                                    >
                                        {INTEREST_OPTIONS.map((o) => (
                                            <option key={o.v} value={o.v}>{o.l}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="overline block">Tell us what you’re imagining</label>
                                    <textarea
                                        data-testid="partner-input-message"
                                        name="message" value={form.message} onChange={onChange} required
                                        rows={5}
                                        className="field-line mt-3 resize-none"
                                        placeholder="A three-day team offsite in a village. A dinner series with 20 seats. A gifting programme for our next launch…"
                                    />
                                </div>
                                <div className="md:col-span-2 flex items-center justify-between gap-6 pt-4">
                                    <p className="text-xs text-aurika-muted max-w-md">
                                        By submitting this form, you agree to be contacted by Aurika Collective Foundation regarding your enquiry. We do not share your details.
                                    </p>
                                    <button
                                        data-testid="partner-submit"
                                        type="submit" disabled={submitting}
                                        className="btn-primary shrink-0 disabled:opacity-60"
                                    >
                                        {submitting ? 'Sending…' : 'Send Message'} <ArrowRight size={16} />
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
